import { authService } from '../pages/login/routes/Login'
import axios from 'axios'
import { loggedoutReasons } from '../constants/statusMessages'

const shouldNavigateToLogout = (errorResponse: any) => {
  if (![401, 403].includes(errorResponse?.response?.status)) {
    return false
  }

  return errorResponse?.request?.responseURL.includes('/api/flows')
}

export const axiosInstance = axios.create({
  timeout: 300000,
  baseURL: '/api',
  headers: {
    'cache-control': 'no-cache',
  },
})

const db = localStorage.getItem('db')

axiosInstance.interceptors.response.use(
  ({ data }) => data,
  (error) => {
    if (shouldNavigateToLogout(error)) {
      sessionStorage.setItem('loggedoutReason', loggedoutReasons.NORMAL_LOGOUT)
      window.location.replace('/logout')
    }
    return Promise.reject(error)
  },
)

axiosInstance.interceptors.request.use(async (config) => {
  const user = await authService.getUser()
  config.headers.authorization = `Bearer ${user.access_token}`
  return config
})

export const getArchivedDocuments = async (contactId: string, limit: string | number) => {
  try {
    const response = await axiosInstance.get(`/signed/documents?contactId=${contactId}&skip=0&limit=${limit}`)
    return response
  } catch (error) {
    throw error.response.data.Message || error.message
  }
}

export const getAttachmentById = async (attachmentId: string, mimeType: string, flowId: string) => {
  return axiosInstance
    .get(`/attachments/${attachmentId}?flowId=${flowId}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': mimeType,
      },
    })
    .catch((error) => {
      throw error.response.data.Message || error.message
    })
}

export const fetchImprovedTableData = async ({
  fromDate,
  toDate,
  account,
  productType,
  companyId,
  contactId,
}: {
  fromDate: string
  toDate: string
  account: string
  productType: string
  companyId: string
  contactId?: string
}) => {
  const url = `/fleet/${companyId}/accounts/${account}/expenses/${productType}?fromDate=${fromDate}&toDate=${toDate}&contactId=${contactId}`
  return axiosInstance
    .get(`${url}`, {
      params: {
        ...(db && { db }),
      },
    })
    .catch(console.error)
}

export const getInsuranceInformation = async ({ accountNo, companyId, contactId }) => {
  return axiosInstance.get(`/fleet/${companyId}/accounts/${accountNo}/insurance?contactId=${contactId}`)
}

export const getAdditionalProducts = async ({
  accountNo,
  companyId,
  expenseId,
  contactId,
}: {
  accountNo: string
  companyId: string
  expenseId: string
  contactId: string
}) => {
  const encodeExpenseId = encodeURIComponent(expenseId)
  const fromFiveYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 5)).toISOString()

  const isDateableExpenseId = ['park', 'fuel', 'service', 'workshop', 'toll'].includes(expenseId)
  const todayISO = new Date().toISOString()
  return axiosInstance
    .get(
      `/fleet/${companyId}/accounts/${accountNo}/expenses/${encodeExpenseId}?contactId=${contactId}${
        isDateableExpenseId ? `&fromDate=${fromFiveYearsAgo}&toDate=${todayISO}` : ''
      }`,
      {
        params: {
          ...(db && { db }),
        },
      },
    )
    .catch(console.error)
}

export const getCarPark = async ({
  companyId,
  consolidationCode,
  filter,
  pageNo,
  pageSize,
}: {
  companyId: string
  consolidationCode?: string
  filter?: string
  pageNo?: number
  pageSize?: number
}) => {
  return axiosInstance
    .get(
      `/fleet/simple?companyId=${companyId}${
        consolidationCode === '_ALL_' ? '' : `&consolidatedInvoice=${consolidationCode}`
      }`,
      {
        params: {
          ...(db && { db }),
          ...(filter && { filter }),
          ...(pageNo && pageSize && { pageNo, pageSize }),
        },
      },
    )
    .catch(console.error)
}

export const getFleetSummary = async ({
  companyId,
  consolidationCode,
}: {
  companyId: string
  consolidationCode?: string
}) => {
  return axiosInstance.get(
    `/fleet/summary?companyId=${companyId}${
      consolidationCode === '_ALL_' ? '' : `&consolidatedInvoice=${consolidationCode}`
    }`,
    {
      params: {
        ...(db && { db }),
      },
    },
  )
}

export const getCarImage = async ({ budgetNo, companyId }: { budgetNo: string; companyId: string }) => {
  return axiosInstance
    .get(`/fleet/${companyId}/budgets/${budgetNo}/pictures`, {
      params: {
        ...(db && { db }),
      },
    })
    .catch(console.error)
}

export const fetchLatestServiceInvoice = async (companyId: string, accountId: string, contactId: string) => {
  return axiosInstance.get(`/fleet/${companyId}/accounts/${accountId}/expenses/service`, {
    params: {
      contactId,
      findFirst: 1,
    },
  })
}

export const sendMessageEmail = async (payload: {
  company: string
  registrationNumber?: string
  name: string
  mail: string
  phone: string
  address?: string
  postCode?: string
  city?: string
  message: string
}) => axiosInstance.post(`/contact-us`, payload)

export const recordMileage = async (accountNumber: string, mileage: number) => {
  return axiosInstance.post('/mileage', { AccNumber: `${accountNumber}`, Mileage: `${mileage}` }).catch((err) => {
    throw err.response.data.Message || err.message
  })
}

export const fetchTollReport = async ({
  companyId,
  contactId,
  consolidatedAgreementID,
  fromDate,
  toDate,
}: {
  companyId: string
  contactId: string
  consolidatedAgreementID: string
  fromDate?: string
  toDate?: string
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/toll`, {
    params: {
      contactId,
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
    },
  })
}

export const fetchReportTermLimits = async ({
  companyId,
  contactId,
  type,
  isLeasing = false,
}: {
  companyId: string
  contactId: string
  type: 'fleet' | 'toll' | 'fuel' | 'invoice'
  isLeasing?: boolean
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/${type}/terms`, {
    params: {
      contactId,
      ...(isLeasing && { isLeasing: 'true' }),
    },
  })
}

export const fetchTollReportDetails = async ({
  companyId,
  contactId,
  consolidatedAgreementID,
  accountNo,
  year,
  month,
}: {
  companyId: string
  contactId: string
  consolidatedAgreementID: string
  accountNo?: string
  year: string
  month: string
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/toll-details`, {
    params: {
      contactId,
      year,
      month,
      ...(consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
      ...(accountNo && { accountNo }),
    },
  })
}

export const fetchTollTransactionDetails = async ({
  companyId,
  contactId,
  consolidatedAgreementID,
  accountNo,
  year,
  month,
}: {
  companyId: string
  contactId: string
  consolidatedAgreementID?: string
  accountNo: string
  year: string
  month: string
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/toll-transaction-details`, {
    params: {
      contactId,
      year,
      month,
      accountNo,
      ...(consolidatedAgreementID && consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
    },
  })
}

export const fetchFuelReport = async ({
  companyId,
  contactId,
  consolidatedAgreementID,
  fromDate,
  toDate,
}: {
  companyId: string
  contactId: string
  consolidatedAgreementID: string
  fromDate?: string
  toDate?: string
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/fuel`, {
    params: {
      contactId,
      fetchAll: true,
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
    },
  })
}

export const fetchFuelReportDetails = async ({
  companyId,
  contactId,
  consolidatedAgreementID,
  year,
  month,
}: {
  companyId: string
  contactId: string
  consolidatedAgreementID: string
  year: string
  month: string
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/fuel-details`, {
    params: {
      contactId,
      year,
      month,
      fetchAll: true,
      ...(consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
    },
  })
}

export const fetchFuelAccountExpensesReport = async ({
  companyId,
  contactId,
  consolidatedAgreementID,
  accountNo,
  year,
  month,
}: {
  companyId: string
  contactId: string
  consolidatedAgreementID?: string
  accountNo: string
  year: string
  month: string
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/fuel-account-expenses`, {
    params: {
      contactId,
      accountNo,
      year,
      fetchAll: true,
      month,
      ...(consolidatedAgreementID && consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
    },
  })
}

export const fetchInvoiceReport = async ({
  companyId,
  contactId,
  fromDate,
  toDate,
  fetchAll = false,
  consolidatedAgreementID,
}: {
  companyId: string
  contactId: string
  fromDate?: string
  toDate?: string
  fetchAll?: boolean
  consolidatedAgreementID?: string
}) => {
  const toDateTime = (value?: string) => {
    if (!value) return value
    if (/Z$|[+-]\d{2}:\d{2}$/.test(value)) return value
    if (value.includes('T')) return `${value}Z`
    return `${value}T00:00:00Z`
  }

  return axiosInstance.get(`/fleet/${companyId}/reports/invoice`, {
    params: {
      contactId,
      ...(fromDate && { fromDate: toDateTime(fromDate) }),
      ...(toDate && { toDate: toDateTime(toDate) }),
      ...(fetchAll && { fetchAll }),
      ...(consolidatedAgreementID && consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
    },
  })
}

export const fetchInvoiceHeadersReport = async ({
  companyId,
  contactId,
  consolidatedInvoiceNo,
  fetchAll = false,
}: {
  companyId: string
  contactId: string
  consolidatedInvoiceNo: string
  fetchAll?: boolean
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/invoice-headers`, {
    params: {
      contactId,
      consolidatedInvoiceNo,
      ...(fetchAll && { fetchAll }),
    },
  })
}

export const fetchInvoiceDetailsReport = async ({
  companyId,
  contactId,
  invoiceNo,
  fetchAll = false,
}: {
  companyId: string
  contactId: string
  invoiceNo: string
  fetchAll?: boolean
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/invoice-details`, {
    params: {
      contactId,
      invoiceNo,
      ...(fetchAll && { fetchAll }),
    },
  })
}

export const fetchFleetReportTerms = async ({
  contactId,
  companyId,
  fetchAll = false,
}: {
  contactId: string
  companyId: string
  fetchAll?: boolean
}) => {
  return axiosInstance.get(`/fleet/${companyId}/reports/fleet/terms?contactId=${contactId}`, {
    params: {
      ...(fetchAll && { fetchAll }),
    },
  })
}

export const fetchFleetReport = async (data: {
  term: string
  companyId: string
  contactId: string
  consolidatedAgreementID: string
  isLeasing?: boolean
}) => {
  const { term, companyId, contactId, consolidatedAgreementID, isLeasing = false } = data
  const normalizedTerm = term?.includes('T') ? term : `${term}T00:00:00Z`

  const query = new URLSearchParams({
    contactId,
    fetchAll: 'true',
    ...(normalizedTerm && { term: normalizedTerm }),
    ...(consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
    ...(isLeasing && { isLeasing: 'true' }),
  }).toString()

  return axiosInstance.get(`/fleet/${companyId}/reports/fleet?${query}`)
}

export const fetchFleetDetailsReport = async (data: {
  term: string
  companyId: string
  contactId: string
  accountNo: string
  consolidatedAgreementID: string
}) => {
  const { term, companyId, contactId, accountNo, consolidatedAgreementID } = data
  const normalizedTerm = term?.includes('T') ? term : `${term}T00:00:00Z`

  const query = new URLSearchParams({
    contactId,
    accountNo,
    ...(normalizedTerm && { term: normalizedTerm }),
    ...(consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
  }).toString()

  return axiosInstance.get(`/fleet/${companyId}/reports/fleet-details?${query}`)
}

export const fetchFleetExpenseReport = async (data: {
  toDate: string
  itemNo: string
  fromDate: string
  companyId: string
  contactId: string
  accountNo: string
  consolidatedAgreementID: string
}) => {
  const { toDate, fromDate, companyId, contactId, accountNo, itemNo, consolidatedAgreementID } = data

  const query = new URLSearchParams({
    contactId,
    accountNo,
    toDate,
    itemNo,
    fromDate,
    ...(consolidatedAgreementID !== '_ALL_' && { consolidatedAgreementID }),
  }).toString()

  return axiosInstance.get(`/fleet/${companyId}/reports/fleet-expenses?${query}`)
}
